/**
 * Raqam maydoni uchun kiritilgan matnni tozalash va formatlash (components/ui/field.tsx: NumberInput). Brauzerning
 * `type="number"` maydoni ishlatilmaydi (strelkalari va "e", "+" kabi belgilarga ruxsati bor): matn maydoni + shu filtr.
 *
 * Qiymat ikki ko'rinishda yuradi: xom (`"1234567.5"`, iste'molchi va API shuni oladi) va ko'rinish (`"1,234,567.5"`,
 * maydonda shu turadi). Ajratgich ilovadagi ko'rsatish formati bilan bir xil (`formatMoney`: en-US -- vergul ming,
 * nuqta kasr). Yozilgan vergul kasr belgisi deb olinadi (o'zbek/rus odati), ko'rinishdagi vergullar esa ajratgich.
 */
export interface NumericRules {
  /** Kasr xonalari soni: 0 -- faqat butun son */
  decimals: number;
  /** Boshida minus bo'lishi mumkinmi */
  negative: boolean;
}

/** Ming ajratgichi (ko'rinishda) */
export const GROUP = ",";

/** "12abc" -> "12", "1,500.5" -> "1500.5" (vergul ajratgich, tushib qoladi), "--3" -> "-3" (negative), "1.2.3" -> "1.23"/"1.2" */
export function sanitizeNumeric(raw: string, { decimals, negative }: NumericRules): string {
  let s = String(raw ?? "");
  const sign = negative && s.trimStart().startsWith("-") ? "-" : "";
  s = s.replace(/[^\d.]/g, "");
  if (decimals > 0) {
    const dot = s.indexOf(".");
    if (dot !== -1) {
      const whole = s.slice(0, dot);
      const fraction = s.slice(dot + 1).replace(/\./g, "").slice(0, decimals);
      s = `${whole}.${fraction}`;
    }
  } else {
    s = s.replace(/\./g, "");
  }
  return sign + s;
}

/** Xom matnni ko'rinishga: "1234567.50" -> "1,234,567.50" (matn asosida: yozilayotgan oxirgi nuqta va nollar saqlanadi) */
export function groupDigits(raw: string): string {
  if (!raw) return "";
  const sign = raw.startsWith("-") ? "-" : "";
  const body = sign ? raw.slice(1) : raw;
  const dot = body.indexOf(".");
  const whole = dot === -1 ? body : body.slice(0, dot);
  const rest = dot === -1 ? "" : body.slice(dot);
  return sign + whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP) + rest;
}

/** Ko'rinishdagi kursor o'rni: `count` ta ma'noli belgi (ajratgichdan boshqa)dan keyin */
export function caretAfter(display: string, count: number): number {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < display.length; i++) {
    if (display[i] === GROUP) continue;
    seen += 1;
    if (seen === count) return i + 1;
  }
  return display.length;
}

/**
 * Qo'yilgan (paste) matn: "1 234,50" va "1 234.50" va "1,234.50" -- bir xil. Bo'shliqlar tushadi; nuqta bo'lmasa va bitta
 * vergul ortidan 1..decimals ta raqam bilan tugasa -- vergul kasr belgisi, aks holda ajratgich.
 */
export function normalizePasted(text: string, decimals: number): string {
  let s = text.replace(/[\s  ']/g, "");
  if (decimals > 0 && !s.includes(".")) {
    const m = s.match(/^(-?\d*),(\d+)$/);
    if (m && m[2]!.length <= decimals) s = `${m[1]}.${m[2]}`;
  }
  return s.replace(/,/g, "");
}

export interface NumericEdit {
  /** Maydondagi matn (brauzer o'zgartirgandan keyin) */
  text: string;
  /** Kursor (`selectionStart`) */
  caret: number;
  /** Oldingi ko'rinish (ajratgich ustida Backspace/Delete uchun) */
  prev?: string;
  /** `InputEvent.inputType` */
  inputType?: string;
  /** `InputEvent.data` (yozilgan belgi) */
  data?: string | null;
  /** Qo'yilgan matn (`onPaste` dan) */
  pasted?: string | null;
}

export interface NumericEditResult {
  raw: string;
  display: string;
  caret: number;
}

const stripGroup = (s: string) => s.split(GROUP).join("");

/** Har o'zgarishda: brauzer qoldirgan matndan xom qiymat, ko'rinish va kursor o'rni */
export function editNumeric(edit: NumericEdit, rules: NumericRules, group = true): NumericEditResult {
  let { text, caret } = edit;
  caret = Math.max(0, Math.min(caret, text.length));

  if (edit.inputType === "insertText" && edit.data === GROUP && caret > 0 && text[caret - 1] === GROUP) {
    // yozilgan vergul -- kasr belgisi (butun sonda shunchaki tushib qoladi)
    const dot = rules.decimals > 0 ? "." : "";
    text = text.slice(0, caret - 1) + dot + text.slice(caret);
    caret -= 1 - dot.length;
  } else if (edit.inputType === "insertFromPaste" && edit.pasted) {
    const start = caret - edit.pasted.length;
    if (start >= 0 && text.slice(start, caret) === edit.pasted) {
      const norm = normalizePasted(edit.pasted, rules.decimals);
      text = text.slice(0, start) + norm + text.slice(caret);
      caret = start + norm.length;
    }
  } else if (group && edit.prev && text.length === edit.prev.length - 1 && stripGroup(text) === stripGroup(edit.prev)) {
    // faqat ajratgich o'chirilgan: Backspace -- oldingi raqam, Delete -- keyingi raqam ketadi
    if (edit.inputType === "deleteContentBackward" && caret > 0) {
      text = text.slice(0, caret - 1) + text.slice(caret);
      caret -= 1;
    } else if (edit.inputType === "deleteContentForward" && caret < text.length) {
      text = text.slice(0, caret) + text.slice(caret + 1);
    }
  }

  const raw = sanitizeNumeric(text, rules);
  const before = sanitizeNumeric(text.slice(0, caret), rules);
  const display = group ? groupDigits(raw) : raw;
  return { raw, display, caret: caretAfter(display, Math.min(before.length, raw.length)) };
}

/** `value` prop'idan ko'rinish: "1234.5" -> "1,234.5"; raqam ham, bo'sh ham bo'lishi mumkin */
export function displayNumeric(value: string | number | null | undefined, rules: NumericRules, group = true): string {
  const raw = sanitizeNumeric(value === null || value === undefined ? "" : String(value), rules);
  return group ? groupDigits(raw) : raw;
}

/** `step="0.01"` -> 2, `step={1}` -> 0, berilmagan -> 0 */
export function decimalsFromStep(step: string | number | undefined): number {
  if (step === undefined || step === null || step === "") return 0;
  const text = String(step);
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}

/** Klaviatura: raqam, kasr belgisi (ruxsat bo'lsa), minus (ruxsat bo'lsa), boshqaruv tugmalari; qolgani bloklanadi */
export function isAllowedNumericKey(key: string, { decimals, negative }: NumericRules, modifier = false): boolean {
  if (modifier || key.length > 1) return true; // Backspace, Tab, strelkalar, Ctrl+V ...
  if (/\d/.test(key)) return true;
  if ((key === "." || key === ",") && decimals > 0) return true;
  return key === "-" && negative;
}
